import React, { useEffect, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";

const STEP_TIMEOUT_MS = 12000;
const EXCHANGE_TIMEOUT_MS = 25000;
const RECOVERY_TIMEOUT_MS = 30000;
const RETURN_TO_STORAGE_KEY = "dp_return_to";

function withTimeout(promise, ms, message) {
  let id;
  const timeout = new Promise((_, reject) => {
    id = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(id));
}

/** Capture code from URL as early as possible (before Supabase or routing can strip it). */
function getCodeFromUrl() {
  if (typeof window === "undefined") return null;
  const url = new URL(window.location.href);
  const fromQuery = url.searchParams.get("code");
  if (fromQuery) return fromQuery;
  const hash = url.hash?.slice(1);
  if (hash) {
    try {
      return new URLSearchParams(hash).get("code") || null;
    } catch {
      return null;
    }
  }
  return null;
}

function normalizeReturnToPath(value) {
  if (typeof value !== "string" || !value.startsWith("/")) return "";

  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) return "";
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return "";
  }
}

function getReturnToFromUrl() {
  if (typeof window === "undefined") return "";
  const url = new URL(window.location.href);
  const fromQuery = normalizeReturnToPath(url.searchParams.get("returnTo") || "");
  if (fromQuery) return fromQuery;

  return normalizeReturnToPath(sessionStorage.getItem(RETURN_TO_STORAGE_KEY) || "");
}

export default function AuthCallback() {
  const didRedirect = useRef(false);
  const codeRef = useRef(getCodeFromUrl());
  const returnToRef = useRef(getReturnToFromUrl());
  const [msg, setMsg] = useState("Completing sign-in...");
  const [canRetry, setCanRetry] = useState(false);

  useEffect(() => {
    let isMounted = true;
    // Use ref from init, or re-read URL once more in case something cleared the ref
    if (!codeRef.current) codeRef.current = getCodeFromUrl();
    if (!returnToRef.current) returnToRef.current = getReturnToFromUrl();
    const code = codeRef.current;
    const returnTo = returnToRef.current || "/";

    // Strip code/state to avoid re-processing on refresh (we've captured code above)
    window.history.replaceState({}, "", "/auth/callback");

    const redirectToDestination = (reason = "") => {
      if (didRedirect.current) return;
      didRedirect.current = true;
      setMsg("Signed in. Redirecting...");
      sessionStorage.removeItem(RETURN_TO_STORAGE_KEY);
      if (reason) {
        console.log(`[AuthCallback] redirecting to ${returnTo} ${reason}`);
      } else {
        console.log(`[AuthCallback] redirecting to ${returnTo}`);
      }
      window.location.replace(returnTo);
    };

    const fail = (e) => {
      console.error("[AuthCallback] failed:", e);
      if (!isMounted) return;
      setMsg(e?.message || "Sign-in did not complete. Click to retry.");
      setCanRetry(true);
    };

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log("[AuthCallback] auth event:", _event, "hasSession:", !!session?.access_token);
      if (session?.access_token) {
        redirectToDestination();
      }
    });

    (async () => {
      try {
        setCanRetry(false);

        // When we have a code, exchange immediately. Do NOT call getSession() first —
        // it can hang, which leaves the user on "Finalizing sign-in...".
        if (code) {
          setMsg("Exchanging code for session...");
          const { error: exErr } = await withTimeout(
            supabase.auth.exchangeCodeForSession(code),
            EXCHANGE_TIMEOUT_MS,
            "Timed out exchanging code for session. Check your connection and try again."
          );
          if (exErr) throw exErr;
          const { data: s2, error: e2 } = await withTimeout(
            supabase.auth.getSession(),
            STEP_TIMEOUT_MS,
            "Timed out reading session after exchange."
          );
          if (e2) console.warn("[AuthCallback] getSession warning:", e2);
          if (s2?.session?.access_token) {
            redirectToDestination();
            return;
          }
          throw new Error("Token exchange succeeded but no session was detected.");
        }

        // No code: keep waiting a bit longer for recovery / verify-email flows.
        setMsg("Finalizing sign-in...");
        const deadline = Date.now() + RECOVERY_TIMEOUT_MS;
        while (Date.now() < deadline) {
          let sessionData = null;
          let sessionError = null;
          try {
            const result = await withTimeout(
              supabase.auth.getSession(),
              STEP_TIMEOUT_MS,
              "Timed out reading session."
            );
            sessionData = result?.data;
            sessionError = result?.error;
          } catch (timeoutErr) {
            sessionError = timeoutErr;
          }
          if (sessionError) console.warn("[AuthCallback] getSession warning:", sessionError);
          if (sessionData?.session?.access_token) {
            redirectToDestination("(session already present)");
            return;
          }
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
        throw new Error(
          "No auth session was detected. If this was a password reset, try opening the latest reset email and follow the link again."
        );

      } catch (e) {
        fail(e);
      }
    })();

    return () => {
      isMounted = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, []);

  return (
    <div style={{ padding: 24 }}>
      <p>{msg}</p>
      {canRetry ? (
        <button
          type="button"
          onClick={() => window.location.assign("/login")}
          style={{ marginTop: 12, padding: "8px 14px" }}
        >
          Back to login
        </button>
      ) : null}
    </div>
  );
}
